// Shared transcript utilities for LiveBridge recognition hooks.
// Extracted from useLiveBridge.js — import wherever transcript manipulation is needed.

export function normalizeLiveBridgeTranscriptText(text, languageCode) {
  const rawText = String(text || "").trim();

  if (!rawText) {
    return "";
  }

  if (String(languageCode || "").toLowerCase().startsWith("zh")) {
    return rawText.replace(/\s+/g, "");
  }

  return rawText.replace(/\s+/g, " ").trim();
}

export function mergeLiveBridgeTranscript(existingText, incomingText, languageCode) {
  const current = normalizeLiveBridgeTranscriptText(existingText, languageCode);
  const nextChunk = normalizeLiveBridgeTranscriptText(incomingText, languageCode);

  if (!nextChunk) {
    return current;
  }

  if (!current) {
    return nextChunk;
  }

  if (current === nextChunk || current.endsWith(nextChunk)) {
    return current;
  }

  if (nextChunk.startsWith(current)) {
    return nextChunk;
  }

  if (String(languageCode || "").toLowerCase().startsWith("zh")) {
    const overlapLength = getChineseOverlapLength(current, nextChunk);
    if (overlapLength > 0) {
      return `${current}${nextChunk.slice(overlapLength)}`;
    }

    return `${current}${nextChunk}`;
  }

  const overlappedEnglish = mergeEnglishWithOverlap(current, nextChunk);
  if (overlappedEnglish) {
    return overlappedEnglish;
  }

  return `${current} ${nextChunk}`.replace(/\s+/g, " ").trim();
}

export function shouldTranslateLiveBridgeDraft(text, languageCode, isListening) {
  const normalized = normalizeLiveBridgeTranscriptText(text, languageCode);

  if (!normalized) {
    return false;
  }

  if (String(languageCode || "").toLowerCase().startsWith("zh")) {
    if (/[。！？]$/.test(normalized)) {
      return true;
    }
    return isListening ? normalized.length >= 8 : normalized.length >= 4;
  }

  const wordCount = normalized.split(/\s+/).filter(Boolean).length;

  if (/[.!?]$/.test(normalized)) {
    return true;
  }

  if (/[,;:]$/.test(normalized)) {
    return true;
  }

  return isListening ? wordCount >= 3 || normalized.length >= 12 : wordCount >= 2 || normalized.length >= 8;
}

function getChineseOverlapLength(current, nextChunk) {
  const maxOverlap = Math.min(current.length, nextChunk.length);

  for (let size = maxOverlap; size > 0; size -= 1) {
    if (current.slice(-size) === nextChunk.slice(0, size)) {
      return size;
    }
  }

  return 0;
}

function mergeEnglishWithOverlap(current, nextChunk) {
  const currentWords = current.split(/\s+/).filter(Boolean);
  const nextWords = nextChunk.split(/\s+/).filter(Boolean);
  const maxOverlap = Math.min(currentWords.length, nextWords.length);

  for (let size = maxOverlap; size > 0; size -= 1) {
    const currentTail = currentWords.slice(-size).join(" ").toLowerCase();
    const nextHead = nextWords.slice(0, size).join(" ").toLowerCase();

    if (currentTail === nextHead) {
      return [...currentWords, ...nextWords.slice(size)].join(" ").replace(/\s+/g, " ").trim();
    }
  }

  return "";
}
